/**
 * Two confirmation patterns for destructive actions, per Phase 5c-1
 * Q2 lock:
 *
 *   - `ConfirmDialog`        — soft-deletes (recoverable for 30 days),
 *                              member removal, leave-org. Just a Yes
 *                              button; the action is reversible OR
 *                              low-stakes enough that typing isn't
 *                              warranted.
 *   - `TypedConfirmDialog`   — irreversible actions (transfer
 *                              ownership, account delete). User has
 *                              to type the entity name verbatim.
 *
 * Both share the same modal shell and busy/error handling.
 */
import { useState, useEffect, type FormEvent, type ReactNode } from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  Input,
  Label,
} from '@/components/ui'

interface ConfirmDialogProps {
  open: boolean
  onClose: () => void
  title: string
  description: ReactNode
  confirmLabel: string
  confirmVariant?: 'default' | 'destructive'
  onConfirm: () => Promise<void> | void
  busy?: boolean
}

export function ConfirmDialog({
  open,
  onClose,
  title,
  description,
  confirmLabel,
  confirmVariant = 'destructive',
  onConfirm,
  busy = false,
}: ConfirmDialogProps) {
  const [error, setError] = useState<string | null>(null)

  async function handleConfirm() {
    setError(null)
    try {
      await onConfirm()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed')
    }
  }

  return (
    // Block dismiss while busy — the action is in flight and dropping
    // the modal mid-request can hide failures or let the user
    // re-trigger from a stale state. Cancel and the close affordances
    // both ignore until the request completes.
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {error && (
          <DialogBody>
            <p className="text-sm text-destructive" role="alert">{error}</p>
          </DialogBody>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant={confirmVariant} onClick={handleConfirm} disabled={busy}>
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface TypedConfirmDialogProps {
  open: boolean
  onClose: () => void
  title: string
  description: ReactNode
  /** What the user has to type verbatim — usually the entity's name. */
  expectedPhrase: string
  confirmLabel: string
  onConfirm: () => Promise<void> | void
  busy?: boolean
}

export function TypedConfirmDialog({
  open,
  onClose,
  title,
  description,
  expectedPhrase,
  confirmLabel,
  onConfirm,
  busy = false,
}: TypedConfirmDialogProps) {
  const [typed, setTyped] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Reset typing state every time the dialog opens — leftover input
  // from a previous open would re-enable the destructive button at
  // mount, defeating the gate.
  useEffect(() => {
    if (open) {
      setTyped('')
      setError(null)
    }
  }, [open])

  const matches = typed === expectedPhrase

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!matches) return
    setError(null)
    try {
      await onConfirm()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed')
    }
  }

  return (
    // Block dismiss while busy — same rationale as ConfirmDialog,
    // doubly important here because the underlying actions are
    // irreversible (transfer ownership, account delete).
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className="space-y-2">
              <Label htmlFor="typed-confirm">
                Type <span className="font-mono font-semibold">{expectedPhrase}</span> to confirm
              </Label>
              <Input
                id="typed-confirm"
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                disabled={busy}
                autoFocus
                autoComplete="off"
              />
            </div>
            {error && <p className="text-sm text-destructive mt-2" role="alert">{error}</p>}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button type="submit" variant="destructive" disabled={!matches || busy}>
              {busy ? 'Working…' : confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
